import {
  Button,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import alqemiDataService from "../../../api/AlqemiDataService";
import alqemiService from "../../../api/AlqemiService";
import Icon from "../../../common/components/fundamentals/Icon";
import { useSnackbar } from "../../../common/context/SnackbarProvider";
import { useUser } from "../../../common/context/UserProvider";
import { FullStack } from "../../../common/styles/Box.styles";
import CVPDF from "../../../cv-generation/CVPDF";
import {
  borderStyle,
  defaultTransition,
  smallGap,
  tinyGap,
} from "../../../styles/consts";
import {
  hasSalesOrAdminAccess,
  replaceWhitespaceForDash,
} from "../../../utils";
import { getDefaultCV } from "../../../utils/cvUtils";
import PopUpBox from "../../components/PopUpBox";
import { useOffer } from "../../context/OfferProvider";
import { useStepNavigation } from "../../context/StepNavigationContext";
import CandidateSelectionRow from "../CandidateSelectionRow";
import CvJsonDiffComparer, { CvJsonDiffComparerHandle } from "../CvJsonDiffComparer";
import KeywordAnalysis from "../KeywordAnalysis";
import StepNavigation from "../StepNavigation";

interface AlqemiSimpleInput {
  jobOffer: any;
  cv: any;
  cvTemplate?: any;
  emailTemplate?: any;
  matchingId?: string;
  existingScore?: number;
}

export default function CVAdaptationStep() {
  const { offer, setOffer } = useOffer();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { offerId } = useParams();
  const { setCandidateStep } = useStepNavigation();
  const { showSnackbar } = useSnackbar();
  const { user } = useUser();

  const [displayedMatchEmail, setDisplayedMatchEmail] = useState("");
  const [language, setLanguage] = useState<"en" | "de">("en");
  const [showRequirementsMatch, setShowRequirementsMatch] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [userTriggeredLanguageChange, setUserTriggeredLanguageChange] =
    useState(false);
  const [translationStartTime, setTranslationStartTime] = useState<
    number | null
  >(null);
  const [translationElapsedSeconds, setTranslationElapsedSeconds] = useState(0);
  const [hideCvDuringTranslation, setHideCvDuringTranslation] = useState(false);
  const [isGenerating] = useState(false);
  const [localIsGeneratingCV, setLocalIsGeneratingCV] = useState(false);
  const [pdfDownloadBlob, setPdfDownloadBlob] = useState<Blob | null>(null);
  const processedCVRef = useRef<string | null>(null);
  const [isRescoring, setIsRescoring] = useState(false);
  const [hasCvUnsavedChanges, setHasCvUnsavedChanges] = useState(false);
  const cvDiffRef = useRef<CvJsonDiffComparerHandle>(null);

  const isGeneratingCV = localIsGeneratingCV;

  const setCurrentCandidateInUrl = (email: string) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("currentCandidate", email);
    window.history.replaceState(null, "", `?${newSearchParams.toString()}`);
  };

  useEffect(() => {
    const currentCandidate = searchParams.get("currentCandidate");

    const candidateExists =
      currentCandidate &&
      offer?.matches?.find(
        (match) => match.candidate?.email === currentCandidate,
      );

    if (candidateExists) {
      if (currentCandidate !== displayedMatchEmail) {
        setDisplayedMatchEmail(currentCandidate);
        setCvContent(null);
      }
    } else if (offer?.matches && offer.matches.length > 0) {
      const highestScoreMatch = offer.matches.find(
        (match) =>
          match.score ===
          Math.max(...(offer.matches?.map((m) => m.score || 0) || [])),
      );
      const defaultEmail =
        highestScoreMatch?.candidate?.email ||
        offer.matches[0]?.candidate?.email ||
        "";
      if (defaultEmail) {
        setDisplayedMatchEmail(defaultEmail);
        setCurrentCandidateInUrl(defaultEmail);
        setCvContent(null);
      }
    }
  }, [searchParams, offer?.matches]);

  const displayedMatch = offer?.matches?.find(
    (match) => match.candidate?.email === displayedMatchEmail,
  );

  useEffect(() => {
    if (displayedMatch?.candidate) {
      const isCandidate = user.email === displayedMatch.candidate.email;
      const isSupervisor =
        user.email === displayedMatch.candidate.supervisor_email;

      if (!isCandidate && !isSupervisor && !hasSalesOrAdminAccess(user)) {
        navigate(
          `/alqemi/${offerId}/review?currentCandidate=${encodeURIComponent(displayedMatchEmail)}`,
        );
      }
    }
  }, [displayedMatch, user.email, navigate, offerId, displayedMatchEmail]);

  const [cvContent, setCvContent] = useState<any>(null);

  const handleGenerateCv = async () => {
    if (displayedMatch?.cv) {
      processedCVRef.current = JSON.stringify(displayedMatch.cv).substring(
        0,
        100,
      );
    } else {
      processedCVRef.current = null;
    }

    setLocalIsGeneratingCV(true);
    showSnackbar("Generating CV", false);

    const candidateId = displayedMatch?.candidate?.id;
    if (!candidateId) {
      showSnackbar("No candidate selected", true);
      setLocalIsGeneratingCV(false);
      return;
    }

    try {
      const candidate = await alqemiDataService.getCandidate(candidateId);
      const cv = getDefaultCV(candidate);

      const simpleInput: AlqemiSimpleInput = {
        jobOffer: offer,
        cv: cv,
        matchingId: displayedMatch?.id,
        existingScore: displayedMatch?.score || 10,
      };

      const response = await alqemiService.createCv(simpleInput);

      if (response && !response.success) {
        showSnackbar(response.message || "Failed to start CV generation", true);
        setLocalIsGeneratingCV(false);
      }
    } catch (error) {
      showSnackbar("Failed to start CV generation. Please try again.", true);
      setLocalIsGeneratingCV(false);
    }
  };

  const handleRescore = async () => {
    if (!displayedMatch?.id) return;
    if (hasCvUnsavedChanges) {
      showSnackbar("Please save your CV changes before re-scoring", true);
      return;
    }
    setIsRescoring(true);
    try {
      const result = await alqemiDataService.rescoreMatch(displayedMatch.id);
      if (result.success) {
        showSnackbar(`Re-scored: ${result.score_consultant_edited}`);
        if (offer && setOffer) {
          const updatedOffer = {
            ...offer,
            matches: offer.matches?.map((match: any) =>
              match.id === displayedMatch.id
                ? {
                    ...match,
                    score_consultant_edited: result.score_consultant_edited,
                  }
                : match,
            ),
          };
          setOffer(updatedOffer);
        }
      } else {
        showSnackbar(result.message || "Re-scoring failed", true);
      }
    } catch (error) {
      showSnackbar("Failed to re-score. Please try again.", true);
    } finally {
      setIsRescoring(false);
    }
  };

  const handleDownloadCV = () => {
    if (pdfDownloadBlob) {
      const aTag = document.createElement("a");
      aTag.href = URL.createObjectURL(pdfDownloadBlob);
      aTag.style.display = "none";
      aTag.download = `Perelyn_Profile_${replaceWhitespaceForDash(
        displayedMatch?.candidate?.name || "Unknown",
      )}_${language.toUpperCase()}.pdf`;
      document.body.appendChild(aTag);
      aTag.click();
    }
  };

  useQuery({
    queryKey: [
      "cv-poll",
      displayedMatch?.id,
      displayedMatch?.updated_at,
      isGeneratingCV,
    ],
    queryFn: async () => {
      if (!isGeneratingCV || !displayedMatch?.id) {
        return null;
      }

      const result = await alqemiDataService.getMatch(
        displayedMatch.id,
        displayedMatch.updated_at,
      );

      const hasCVContent =
        result.data?.cv && Object.keys(result.data.cv).length > 0;

      const cvHash = hasCVContent
        ? JSON.stringify(result.data.cv).substring(0, 100)
        : null;
      const isNewCV = cvHash && cvHash !== processedCVRef.current;

      if (hasCVContent && isNewCV) {
        processedCVRef.current = cvHash;
        await alqemiDataService.updateMatch(
          displayedMatch.id,
          "status",
          "viewed",
        );

        setCvContent(result.data.cv);

        if (offer && setOffer) {
          const updatedOffer = {
            ...offer,
            matches: offer.matches?.map((match) =>
              match.id === displayedMatch.id
                ? {
                    ...match,
                    cv: result.data.cv,
                    email: result.data.email || match.email,
                    keyword_analysis:
                      result.data.keyword_analysis ?? match.keyword_analysis,
                  }
                : match,
            ),
          };
          setOffer(updatedOffer);
        }

        showSnackbar("CV generation completed!", false, false);
        setLocalIsGeneratingCV(false);
        return result.data;
      }

      return null;
    },
    enabled: isGeneratingCV,
    refetchInterval: isGeneratingCV ? 5000 : false,
    retry: false,
  });

  useEffect(() => {
    if (displayedMatch?.cv && !hideCvDuringTranslation) {
      setCvContent(displayedMatch.cv);
    } else if (displayedMatch && !displayedMatch.cv && !hideCvDuringTranslation) {
      setCvContent({});
    }
  }, [
    displayedMatch?.candidate?.email,
    displayedMatch?.cv,
    hideCvDuringTranslation,
  ]);

  useEffect(() => {
    if (!isTranslating) {
      setLanguage(displayedMatch?.cv_language || "en");
    }
  }, [
    displayedMatch?.candidate?.email,
    displayedMatch?.cv_language,
    isTranslating,
  ]);

  useEffect(() => {
    if (displayedMatch) {
      setShowRequirementsMatch(displayedMatch.requirementsMatch?.show ?? false);
    }
  }, [
    displayedMatch?.candidate?.email,
    displayedMatch?.requirementsMatch?.show,
  ]);

  useEffect(() => {
    const autoHideRequirementsMatch = async () => {
      if (
        !displayedMatch?.id ||
        !displayedMatch.cv ||
        isGeneratingCV ||
        isTranslating
      ) {
        return;
      }

      if (
        displayedMatch.cv.requirementsMatch &&
        !displayedMatch.requirementsMatch
      ) {
        try {
          const updatedCv = { ...displayedMatch.cv };
          const requirementsMatchData = {
            content: updatedCv.requirementsMatch,
            show: false,
          };
          delete updatedCv.requirementsMatch;

          const [saveResult] = await Promise.all([
            alqemiDataService.saveCv(displayedMatch.id, updatedCv),
            alqemiDataService.updateMatch(
              displayedMatch.id,
              "requirementsMatch",
              requirementsMatchData,
            ),
          ]);

          setCvContent(updatedCv);
          setShowRequirementsMatch(false);

          if (offer && setOffer) {
            const updatedOffer = {
              ...offer,
              matches: offer.matches?.map((match) =>
                match.id === displayedMatch.id
                  ? {
                      ...match,
                      cv: updatedCv,
                      keyword_analysis:
                        saveResult.keyword_analysis ?? match.keyword_analysis,
                      requirementsMatch: requirementsMatchData,
                    }
                  : match,
              ),
            };
            setOffer(updatedOffer);
          }
        } catch (error) {
          console.error("Failed to auto-hide requirements match:", error);
        }
      }
    };

    autoHideRequirementsMatch();
  }, [
    displayedMatch?.id,
    displayedMatch?.cv,
    displayedMatch?.requirementsMatch,
    displayedMatch?.candidate?.email,
    isGeneratingCV,
    isTranslating,
    offer,
    setOffer,
  ]);

  useEffect(() => {
    if (!translationStartTime) return;
    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - translationStartTime) / 1000);
      setTranslationElapsedSeconds(elapsed);
    }, 1000);
    return () => clearInterval(interval);
  }, [translationStartTime]);

  useEffect(() => {
    const translateCV = async () => {
      if (!displayedMatch) return;

      const matchId = displayedMatch.id || displayedMatch.consultant_id;
      if (!matchId) return;

      setIsTranslating(true);
      setHideCvDuringTranslation(true);
      setTranslationStartTime(Date.now());
      setTranslationElapsedSeconds(0);

      await alqemiDataService.updateMatch(matchId, "cv_language", language);

      try {
        const result = await alqemiDataService.translateCV(matchId, language);
        if (result.status === "success") {
          const MAX_POLL_ATTEMPTS = 60;
          let attempts = 0;
          while (attempts < MAX_POLL_ATTEMPTS) {
            attempts++;
            await new Promise((resolve) => setTimeout(resolve, 3000));

            const updatedMatch = await alqemiDataService.getMatch(matchId);
            if (
              updatedMatch?.data?.translation_status === "completed" &&
              updatedMatch?.data?.cv &&
              updatedMatch?.data?.cv_language === language
            ) {
              const updatedOffer = { ...offer };
              if (updatedOffer.matches) {
                const matchIndex = updatedOffer.matches.findIndex(
                  (m) => m.candidate?.email === displayedMatch.candidate?.email,
                );
                if (matchIndex !== -1) {
                  updatedOffer.matches[matchIndex] = {
                    ...updatedOffer.matches[matchIndex],
                    cv: updatedMatch.data.cv,
                    cv_language: language,
                    translation_status: "completed",
                  };
                  setOffer(updatedOffer);
                  setCvContent(updatedMatch.data.cv);
                }
              }

              setIsTranslating(false);
              setHideCvDuringTranslation(false);
              setUserTriggeredLanguageChange(false);
              setTranslationStartTime(null);
              setTranslationElapsedSeconds(0);
              break;
            }
          }
          if (attempts >= MAX_POLL_ATTEMPTS) {
            setIsTranslating(false);
            setHideCvDuringTranslation(false);
            setUserTriggeredLanguageChange(false);
            setTranslationStartTime(null);
            setTranslationElapsedSeconds(0);
            showSnackbar(
              "Translation timed out. Please refresh and try again.",
              true,
            );
          }
        }
      } catch (error) {
        console.error("Error translating CV:", error);
        setIsTranslating(false);
        setHideCvDuringTranslation(false);
        setUserTriggeredLanguageChange(false);
        setTranslationStartTime(null);
        setTranslationElapsedSeconds(0);
      }
    };

    if (
      userTriggeredLanguageChange &&
      displayedMatch &&
      displayedMatch.cv_language !== language
    ) {
      translateCV();
    }
  }, [language, userTriggeredLanguageChange, displayedMatch, offer, setOffer]);

  const handleCandidateClick = async (email: string) => {
    if (email === displayedMatchEmail) return;
    const saved = await autoSaveCvIfNeeded();
    if (!saved) return;

    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("currentCandidate", email);
    window.history.replaceState(null, "", `?${newSearchParams.toString()}`);
    setDisplayedMatchEmail(email);
    setUserTriggeredLanguageChange(false);
    setHideCvDuringTranslation(false);
  };

  const handleToggleRequirementsMatch = async (show: boolean) => {
    if (!displayedMatch?.id || !displayedMatch.cv) return;

    setShowRequirementsMatch(show);

    try {
      let updatedCv = { ...displayedMatch.cv };
      let requirementsMatchData = displayedMatch.requirementsMatch;

      if (!show) {
        if (updatedCv.requirementsMatch) {
          requirementsMatchData = {
            content: updatedCv.requirementsMatch,
            show: false,
          };
          delete updatedCv.requirementsMatch;
        }
      } else {
        if (requirementsMatchData?.content) {
          updatedCv.requirementsMatch = requirementsMatchData.content;
          requirementsMatchData = {
            content: requirementsMatchData.content,
            show: true,
          };
        }
      }

      const [saveResult] = await Promise.all([
        alqemiDataService.saveCv(displayedMatch.id, updatedCv),
        alqemiDataService.updateMatch(
          displayedMatch.id,
          "requirementsMatch",
          requirementsMatchData,
        ),
      ]);

      setCvContent(updatedCv);

      if (offer && setOffer) {
        const updatedOffer = {
          ...offer,
          matches: offer.matches?.map((match) =>
            match.id === displayedMatch.id
              ? {
                  ...match,
                  cv: updatedCv,
                  keyword_analysis:
                    saveResult.keyword_analysis ?? match.keyword_analysis,
                  requirementsMatch: requirementsMatchData,
                }
              : match,
          ),
        };
        setOffer(updatedOffer);
      }

      showSnackbar(
        `Requirements Match ${show ? "enabled" : "disabled"}`,
        false,
      );
    } catch (error) {
      console.error("Failed to update requirements match toggle:", error);
      showSnackbar("Failed to update requirements match", true);
      setShowRequirementsMatch(!show);
    }
  };

  const autoSaveCvIfNeeded = async () => {
    if (hasCvUnsavedChanges && cvDiffRef.current) {
      try {
        await cvDiffRef.current.save();
      } catch (error) {
        console.error("Failed to auto-save CV:", error);
        showSnackbar("Failed to save CV changes. Please fix any errors and try again.", true);
        return false;
      }
    }
    return true;
  };

  const handleNext = async () => {
    const saved = await autoSaveCvIfNeeded();
    if (!saved) return;

    const isCandidate = displayedMatch?.candidate
      ? user.email === displayedMatch.candidate.email
      : false;
    const isSupervisor = displayedMatch?.candidate
      ? user.email === displayedMatch.candidate.supervisor_email
      : false;

    if (isCandidate) {
      setCandidateStep(displayedMatchEmail, 3);
      navigate(
        `/alqemi/${offerId}/feedback?currentCandidate=${encodeURIComponent(displayedMatchEmail)}`,
      );
    } else if (isSupervisor || hasSalesOrAdminAccess(user)) {
      setCandidateStep(displayedMatchEmail, 3);
      navigate(
        `/alqemi/${offerId}/email-review?currentCandidate=${encodeURIComponent(displayedMatchEmail)}`,
      );
    }
  };

  const handlePrevious = async () => {
    const saved = await autoSaveCvIfNeeded();
    if (!saved) return;

    setCandidateStep(displayedMatchEmail, 1);
    navigate(
      `/alqemi/${offerId}/review?currentCandidate=${encodeURIComponent(displayedMatchEmail)}`,
    );
  };

  if (!offer || !offer.matches) {
    return (
      <FullStack sx={{ justifyContent: "center", alignItems: "center" }}>
        <Typography>Loading offer data...</Typography>
      </FullStack>
    );
  }

  return (
    <FullStack
      sx={{ display: "flex", flexDirection: "column", overflow: "hidden" }}
    >
      <Stack
        sx={{ padding: smallGap, gap: smallGap, overflow: "auto", flex: 1 }}
      >
        <Stack
          sx={{
            flexDirection: "row",
            gap: smallGap,
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <CandidateSelectionRow
            matches={offer?.matches}
            displayedMatchEmail={displayedMatchEmail}
            onCandidateClick={handleCandidateClick}
            disabled={isTranslating}
            showDetailedScores={hasSalesOrAdminAccess(user)}
            sx={{ flex: 1, minWidth: 0 }}
          />

          {displayedMatch?.keyword_analysis && (
            <PopUpBox
              button={<Button variant="outlined">Keyword Coverage</Button>}
              heading=""
              width="50%"
              maxHeight="80%"
            >
              <KeywordAnalysis
                keywordAnalysis={displayedMatch.keyword_analysis}
                baselineKeywordAnalysis={displayedMatch.baseline_keyword_analysis}
              />
            </PopUpBox>
          )}

          <PopUpBox
            button={<Button variant="outlined">View Job Description</Button>}
            heading="Job Description"
            width="60%"
            maxHeight="80%"
          >
            <Stack sx={{ height: "100%", overflow: "auto" }}>
              <TextField
                multiline
                value={offer?.beschreibung_translated || offer?.beschreibung || ""}
                sx={{
                  width: "100%",
                  "& .MuiInputBase-root": {
                    minHeight: "20rem",
                  },
                  "& .MuiInputBase-input": {
                    overflow: "auto !important",
                    whiteSpace: "pre-wrap",
                  },
                }}
                InputProps={{
                  readOnly: true,
                }}
              />
            </Stack>
          </PopUpBox>
        </Stack>

        <Stack
          sx={{
            flexDirection: "row",
            gap: smallGap,
            height: "calc(100vh - 240px)",
            minHeight: "660px",
          }}
        >
          <Stack
            sx={{
              width: "50%",
              transition: defaultTransition,
              border: borderStyle,
            }}
          >
            {hideCvDuringTranslation ? (
              <Stack
                sx={{
                  height: "100%",
                  justifyContent: "center",
                  alignItems: "center",
                  backgroundColor: "#f5f5f5",
                  borderRadius: "8px",
                }}
              >
                <CircularProgress size={48} variant="indeterminate" />
                <Typography sx={{ mt: 2, fontSize: "1.1rem", color: "#666" }}>
                  Translating CV to {language === "de" ? "German" : "English"}
                  ...
                </Typography>
                <Typography sx={{ mt: 1, fontSize: "0.9rem", color: "#999" }}>
                  {translationElapsedSeconds > 0
                    ? `Elapsed time: ${Math.floor(
                        translationElapsedSeconds / 60,
                      )}:${String(translationElapsedSeconds % 60).padStart(
                        2,
                        "0",
                      )}`
                    : "This may take a minute or two"}
                </Typography>
              </Stack>
            ) : (
              <CVPDF
                cvContent={cvContent}
                setPdfDownloadBlob={setPdfDownloadBlob}
                isGenerating={isGenerating}
                isGeneratingCV={isGeneratingCV}
                language={language}
                displayedMatchScore={displayedMatch?.score}
              />
            )}
          </Stack>

          <Stack sx={{ width: "50%" }}>
            {displayedMatch && (
              <CvJsonDiffComparer
                ref={cvDiffRef}
                cvContent={cvContent}
                displayedMatch={displayedMatch}
                setCvContent={setCvContent}
                offer={offer}
                setOffer={setOffer}
                onUnsavedChangesChange={setHasCvUnsavedChanges}
              />
            )}
          </Stack>
        </Stack>

        <Stack
          sx={{ flexDirection: "row", gap: smallGap, alignItems: "center" }}
        >
          {(cvContent !== null && Object.keys(cvContent).length === 0) ||
          displayedMatch?.metadata?.cv_generation_failed === true ? (
            <Tooltip
              placement="right"
              title={
                displayedMatch?.metadata?.cv_generation_failed === true
                  ? "Enhancement failed — re-generate to retry"
                  : ""
              }
            >
              <span>
                <Button
                  variant="outlined"
                  disabled={isGeneratingCV || isTranslating}
                  onClick={handleGenerateCv}
                >
                  {isGeneratingCV ? <CircularProgress size={20} /> : "Generate CV"}
                </Button>
              </span>
            </Tooltip>
          ) : (
            <>
              <Button
                variant="outlined"
                id="download-cv"
                onClick={handleDownloadCV}
                disabled={!pdfDownloadBlob || isTranslating}
                sx={{
                  padding: tinyGap,
                  gap: "0.5rem",
                }}
              >
                CV
                <Icon
                  sx={{
                    height: "1.5rem",
                    width: "1.5rem",
                  }}
                  name="Download"
                />
              </Button>

              <FormControlLabel
                control={
                  <Switch
                    checked={language === "de"}
                    onChange={(e) => {
                      const newLang = e.target.checked ? "de" : "en";
                      setLanguage(newLang);
                      setUserTriggeredLanguageChange(true);
                    }}
                    color="primary"
                    disabled={isTranslating}
                  />
                }
                label={
                  isTranslating
                    ? "Translating..."
                    : language === "en"
                      ? "EN"
                      : "DE"
                }
                labelPlacement={language === "en" ? "start" : "end"}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={showRequirementsMatch}
                    onChange={(e) =>
                      handleToggleRequirementsMatch(e.target.checked)
                    }
                    color="primary"
                    disabled={isTranslating}
                  />
                }
                label="Requirements Match"
              />

              {hasSalesOrAdminAccess(user) && (
                <Tooltip
                  title={hasCvUnsavedChanges ? "Save your CV changes before re-scoring" : ""}
                  arrow
                >
                  <span>
                    <Button
                      variant="outlined"
                      onClick={handleRescore}
                      disabled={isRescoring || !displayedMatch?.cv || hasCvUnsavedChanges}
                      startIcon={
                        isRescoring ? <CircularProgress size={16} /> : null
                      }
                    >
                      {isRescoring ? "Re-scoring..." : "Re-score"}
                    </Button>
                  </span>
                </Tooltip>
              )}

            </>
          )}
        </Stack>
      </Stack>

      <StepNavigation
        candidateEmail={displayedMatchEmail}
        onNext={handleNext}
        onPrevious={handlePrevious}
        onBeforeStepChange={autoSaveCvIfNeeded}
        nextDisabled={!displayedMatch || isTranslating}
      />
    </FullStack>
  );
}
